import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Collapse, Divider, Skeleton, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerInitiativeDrawer from "./center/OwnerInitiativeDrawer";
import OwnerCommitmentTimeDialog from "./center/OwnerCommitmentTimeDialog";
import OwnerProjectTaskDialog from "./projects/OwnerProjectTaskDialog";
import { insetSx, surfaceSx } from "./center/ownerCenterUi";
import OwnerProjectWorkPanel from "./projects/OwnerProjectWorkPanel";
import OwnerProjectAside from "./projects/OwnerProjectAside";
import OwnerProjectPeopleLinkPanel from "./projects/OwnerProjectPeopleLinkPanel";
import OwnerProjectEconomicsPanel from "./projects/OwnerProjectEconomicsPanel";
import OwnerProjectAgreementPanel from "./projects/OwnerProjectAgreementPanel";
import {
  PROJECT_DOMAIN_LABELS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_STATUS_LABELS,
  dateOnlyLabel,
} from "./projects/ownerProjectUi";

function ProjectLoading() {
  return <Stack spacing={1.2}><Skeleton variant="rounded" height={92} /><Skeleton variant="rounded" height={112} /><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,2fr) minmax(300px,1fr)" }, gap: 1.2 }}><Skeleton variant="rounded" height={420} /><Skeleton variant="rounded" height={420} /></Box></Stack>;
}

export default function OwnerProject360Page() {
  const { initiativeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [context, setContext] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [notFound, setNotFound] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [activityOpen, setActivityOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState(null);
  const [timeCommitment, setTimeCommitment] = React.useState(null);
  const [economics, setEconomics] = React.useState(null);
  const [economicsLoading, setEconomicsLoading] = React.useState(false);
  const [economicsError, setEconomicsError] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [productHealth, setProductHealth] = React.useState(null);
  const [productHealthLoading, setProductHealthLoading] = React.useState(false);
  const [productHealthError, setProductHealthError] = React.useState("");
  const [activeSection, setActiveSection] = React.useState("work");
  const [showDetails, setShowDetails] = React.useState(false);
  const [showEconomics, setShowEconomics] = React.useState(false);
  const jarvisUiHandledRef = React.useRef("");
  const jarvisOpenedFormRef = React.useRef("");

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (!initiativeId) return;
    if (!silent) setLoading(true);
    setError(""); setNotFound(false);
    try {
      const response = await ownerApi.initiativeContext(initiativeId);
      const nextContext = response?.context || null;
      setContext(nextContext);
      setEconomics(nextContext?.economics || null);
      setEconomicsError(nextContext?.metadata?.economicsError ? "No pude calcular la economía del proyecto." : "");
    } catch (err) {
      setContext(null);
      if (err?.status === 404) setNotFound(true);
      else setError(formatApiError(err, "No pude cargar este proyecto."));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [initiativeId]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const refreshFromJarvis = () => { load({ silent: true }); };
    window.addEventListener("owner:jarvis-mutated", refreshFromJarvis);
    return () => window.removeEventListener("owner:jarvis-mutated", refreshFromJarvis);
  }, [load]);

  const loadProductHealth = React.useCallback(async () => {
    if (!initiativeId) return;
    setProductHealthLoading(true);
    setProductHealthError("");
    try {
      const response = await ownerApi.initiativeProductHealth(initiativeId, { days: 30 });
      setProductHealth(response?.health || null);
    } catch (err) {
      setProductHealth(null);
      setProductHealthError(formatApiError(err, "No pude leer el uso de WebProfit para este proyecto."));
    } finally {
      setProductHealthLoading(false);
    }
  }, [initiativeId]);

  React.useEffect(() => {
    if (activeSection === "overview") loadProductHealth();
  }, [activeSection, loadProductHealth]);

  const retryEconomics = React.useCallback(async () => {
    if (!initiativeId) return;
    setEconomicsLoading(true); setEconomicsError("");
    try {
      const response = await ownerApi.initiativeEconomics(initiativeId);
      setEconomics(response?.economics || null);
    } catch (err) {
      setEconomicsError(formatApiError(err, "No pude calcular la economía del proyecto."));
    } finally {
      setEconomicsLoading(false);
    }
  }, [initiativeId]);

  const goBack = () => {
    if (typeof window !== "undefined" && Number(window.history?.state?.idx || 0) > 0) navigate(-1);
    else navigate("/dashboard/owner");
  };

  const initiative = context?.initiative || null;
  const client = context?.client || null;
  const jarvisKeywordsBySection = {
    work: ["pendientes", "anotar", "tarea", "fix", "esperando", "bloqueado", "eliminar pendiente 2", "stand by"],
    money: ["cobros", "mensualidad", "contrato", "cuotas", "billing", "WebProfit"],
  };
  const jarvisKeywords = jarvisKeywordsBySection[activeSection] || jarvisKeywordsBySection.work;

  const openProjectSection = React.useCallback((section) => {
    const normalized = String(section || "").toLowerCase();
    if (["project-money", "project-billing", "project-contract", "project-contract-recurring", "project-economics", "project-webprofit"].includes(normalized)) setActiveSection("money");
    else setActiveSection("work");
  }, []);

  const selectProjectSection = React.useCallback((section) => {
    setActiveSection(section);
    if (location.state?.ownerJarvisUi) {
      navigate(`${location.pathname}${location.search}${location.hash || ""}`, {
        replace: true,
        state: { ...location.state, ownerJarvisUi: null },
      });
    }
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  React.useEffect(() => {
    const ui = location.state?.ownerJarvisUi || null;
    if (!ui || ui.surface !== "project360" || String(ui.initiativeId || "") !== String(initiativeId || "")) return;
    const token = String(ui.token || `${ui.form || "project"}:${ui.taskId || ""}:${ui.section || ""}`);
    if (jarvisUiHandledRef.current === token) return;
    if (ui.section) openProjectSection(ui.section);

    if (ui.form === "project_edit") {
      setEditOpen(true);
      jarvisOpenedFormRef.current = "project";
      jarvisUiHandledRef.current = token;
      return;
    }
    if (ui.form === "task_new") {
      setEditingTask(null);
      setActivityOpen(true);
      jarvisOpenedFormRef.current = "task";
      jarvisUiHandledRef.current = token;
      return;
    }
    if (ui.form === "task_edit") {
      if (!context) return;
      const rows = [...(context?.steps?.items || []), ...(context?.commitments?.items || [])];
      const task = rows.find((row) => String(row?.id || row?._id || "") === String(ui.taskId || "")) || null;
      if (ui.taskId && !task) return;
      setEditingTask(task);
      setActivityOpen(true);
      jarvisOpenedFormRef.current = "task";
      jarvisUiHandledRef.current = token;
      return;
    }

    if (jarvisOpenedFormRef.current === "task") {
      setActivityOpen(false);
      setEditingTask(null);
    }
    if (jarvisOpenedFormRef.current === "project") setEditOpen(false);
    jarvisOpenedFormRef.current = "";
    jarvisUiHandledRef.current = token;
  }, [context, initiativeId, location.state, openProjectSection]);

  const deleteProject = async () => {
    if (!initiative || deleting) return;
    const confirmed = window.confirm(`¿Eliminar ${initiative.name}?\n\nSe eliminará la ficha, contrato y bloques del proyecto. Los movimientos, pagos, tareas y horas trabajadas NO se borran: quedan desvinculados para conservar el historial.`);
    if (!confirmed) return;
    setDeleting(true);
    setError("");
    try {
      await ownerApi.deleteInitiative(initiativeId);
      navigate("/dashboard/owner/proyectos", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar el proyecto."));
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1580, mx: "auto", pb: 1, minWidth: 0 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} sx={{ mb: 1, px: .4, textTransform: "none", fontWeight: 900 }}>Volver</Button>

      {loading ? <ProjectLoading /> : null}
      {!loading && notFound ? <Box sx={{ ...surfaceSx, p: { xs: 2, md: 3 }, textAlign: "center" }}><Typography sx={{ fontSize: "1.15rem", fontWeight: 1000 }}>Este proyecto no existe o ya no está disponible.</Typography><Typography sx={{ mt: .45, color: "text.secondary", fontSize: ".82rem" }}>Puede haber sido eliminado o no pertenecer a tu espacio Owner.</Typography><Button variant="contained" onClick={() => navigate("/dashboard/owner")} sx={{ mt: 1.4, textTransform: "none", fontWeight: 950 }}>Volver a Owner</Button></Box> : null}
      {!loading && error ? <Alert severity="error" action={<Button size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}

      {!loading && context && initiative ? <Stack spacing={1.05}>
        <Box data-owner-project-section="project-overview" sx={{ ...surfaceSx, p: { xs: 1.1, sm: 1.45, md: 1.75 }, minWidth: 0 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: .9, md: 1.1 }} alignItems={{ md: "center" }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="h1" sx={{ fontSize: { xs: "1.3rem", sm: "1.45rem", md: "1.8rem" }, lineHeight: 1.08, fontWeight: 1000, letterSpacing: "-.035em", overflowWrap: "anywhere" }}>{initiative.name}</Typography>
              <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: .65 }}>
                <Chip size="small" label={PROJECT_STATUS_LABELS[initiative.status] || "Estado definido"} sx={{ height: { xs: 24, sm: 26 }, fontWeight: 900, fontSize: { xs: ".68rem", sm: ".75rem" } }} />
                <Chip size="small" variant="outlined" label={`Prioridad ${PROJECT_PRIORITY_LABELS[initiative.priority] || "normal"}`} sx={{ height: { xs: 24, sm: 26 }, fontWeight: 850, fontSize: { xs: ".68rem", sm: ".75rem" } }} />
              </Stack>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .45, color: "text.secondary", fontSize: ".76rem", overflowWrap: "anywhere" }}>{client ? client.name : "Sin persona vinculada"}{initiative.domain ? ` · ${PROJECT_DOMAIN_LABELS[initiative.domain] || "Otro"}` : ""}{initiative.startedAt ? ` · Inicio ${dateOnlyLabel(initiative.startedAt)}` : ""}</Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", md: "auto" }, display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr) minmax(0,1fr)", md: "auto auto" }, gap: .55 }}>
              <Button variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => { setEditingTask(null); setActivityOpen(true); }} sx={{ minHeight: { xs: 42, md: 36 }, minWidth: 0, px: { xs: 1, md: 2 }, textTransform: "none", fontWeight: 950 }}>Anotar</Button>
              <Button variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => setEditOpen(true)} sx={{ minHeight: { xs: 42, md: 36 }, minWidth: 0, px: { xs: 1, md: 2 }, textTransform: "none", fontWeight: 950 }}>Editar</Button>
            </Box>
          </Stack>

        </Box>

        <Box sx={{ ...surfaceSx, p: .55, position: { md: "sticky" }, top: { md: 72 }, zIndex: 4, minWidth: 0 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: .45, minWidth: 0 }}>
            {[
              ["work", "Pendientes"],
              ["money", "Cobros"],
            ].map(([value, label]) => (
              <Button key={value} size="small" variant={activeSection === value ? "contained" : "text"} onClick={() => selectProjectSection(value)} sx={{ minHeight: { xs: 40, sm: 34 }, textTransform: "none", fontWeight: 950, minWidth: 0, px: { xs: .6, sm: 1.15 }, fontSize: { xs: ".76rem", sm: ".8125rem" } }}>{label}</Button>
            ))}
          </Box>
          <Typography sx={{ display: { xs: "none", sm: "block" }, px: .75, pt: .35, pb: .15, color: "text.secondary", fontSize: ".64rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Jarvis entiende acá: {jarvisKeywords.map((word) => `“${word}”`).join(" · ")}
          </Typography>
        </Box>

        {activeSection === "work" ? <Box data-owner-project-section="project-work">
          <OwnerProjectWorkPanel
            commitments={context.commitments}
            steps={context.steps}
            workPlan={context.workPlan}
            meetings={context.meetings}
            onNewActivity={() => { setEditingTask(null); setActivityOpen(true); }}
            onCreateSteps={async (steps) => {
              try { await ownerApi.createProjectSteps(initiativeId, steps); await load({ silent: true }); }
              catch (err) { throw new Error(formatApiError(err, "No pude agregar los pasos.")); }
            }}
            onReorderSteps={async (stepIds) => {
              try { await ownerApi.reorderProjectSteps(initiativeId, stepIds); await load({ silent: true }); }
              catch (err) { throw new Error(formatApiError(err, "No pude guardar el orden de los pasos.")); }
            }}
            onDeleteStep={async (step) => {
              try { await ownerApi.deleteProjectStep(initiativeId, step.id); await load({ silent: true }); }
              catch (err) { throw new Error(formatApiError(err, "No pude sacar ese paso del plan.")); }
            }}
            onEdit={(task) => { setEditingTask(task); setActivityOpen(true); }}
            onTime={setTimeCommitment}
            onState={async (task, state) => {
              if (!task?.id) return;
              try {
                await ownerApi.updateCommitment(task.id, { work: { ...(task.work || {}), state, waitingFor: "", reviewAt: null } });
                await load();
              } catch (err) { setError(formatApiError(err, "No pude cambiar el estado de la tarea.")); }
            }}
            onDone={async (task) => {
              if (!task?.id) return;
              try { await ownerApi.updateCommitment(task.id, { status: "done" }); await load(); }
              catch (err) { setError(formatApiError(err, "No pude completar esta tarea.")); }
            }}
          />
        </Box> : null}

        {activeSection === "money" ? <Stack spacing={.8} data-owner-project-section="project-money">
          <OwnerProjectAgreementPanel initiativeId={initiativeId} focusSection={location.state?.ownerJarvisUi?.section || ""} jarvisUi={location.state?.ownerJarvisUi || null} />
          <Box sx={{ ...surfaceSx, p: .75 }}>
            <Button size="small" onClick={() => setShowEconomics((value) => !value)} sx={{ textTransform: "none", fontWeight: 900 }}>{showEconomics ? "Ocultar economía detallada" : "Ver economía detallada"}</Button>
            <Collapse in={showEconomics} unmountOnExit><Box sx={{ mt: .65 }}><OwnerProjectEconomicsPanel economics={economics} loading={economicsLoading} error={economicsError} onRetry={retryEconomics} onNavigate={(path) => path && navigate(path)} /></Box></Collapse>
          </Box>
        </Stack> : null}
      </Stack> : null}

      <OwnerInitiativeDrawer open={editOpen} initiative={initiative} initialValues={location.state?.ownerJarvisUi?.form === "project_edit" ? location.state?.ownerJarvisUi?.preset : null} onClose={() => setEditOpen(false)} onChanged={async () => { setEditOpen(false); await Promise.all([load(), loadProductHealth()]); }} />
      <OwnerProjectTaskDialog
        open={activityOpen}
        onClose={() => { setActivityOpen(false); setEditingTask(null); }}
        onSaved={async () => { setActivityOpen(false); setEditingTask(null); await load(); }}
        initiative={initiative}
        task={editingTask}
        tasks={context?.steps?.items || context?.commitments?.items || []}
      />
      <OwnerCommitmentTimeDialog open={Boolean(timeCommitment)} commitment={timeCommitment} onClose={() => setTimeCommitment(null)} onSaved={async () => { setTimeCommitment(null); await load(); }} />
    </Box>
  );
}
